import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import { Node } from 'types';

import checkIcon from 'assets/images/check.svg';
import { LANGUAGE_TYPE } from 'consts';
import { useAppStore, useLoginPageStore } from 'hooks';
import { applyStyle } from 'utils';

import styles from './LanguagesList.module.scss';

interface LanguageProps {
  code: string;
  nativeName: string;
  isDefault: boolean;
  latinCode?: string;
}

interface Props {
  languagesHandle?(value: string, type?: string, callBack?: (value: string) => void): void;
  defaultValue: string;
  list: LanguageProps[];
  type: string;
  isDropDown?: boolean;
}

function LanguagesList({ defaultValue, list, isDropDown, type, languagesHandle }: Props) {
  const [language, setLanguage] = useState(defaultValue);
  const intl = useIntl();
  const {
    appStore: {
      localizationStore: { setDisplayLanguage, setAudioLanguage, setSubTitleLanguage, languageNativeNameByCode },
    },
  } = useAppStore();

  const loginPageStore = useLoginPageStore();

  const handleRef = useCallback((node: Node) => {
    const { uiDesignElements } = loginPageStore;

    if (isDropDown && uiDesignElements && node !== null) {
      applyStyle(node, '--background-color', uiDesignElements.backgroundColor);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    setLanguage(defaultValue);
  }, [defaultValue, setLanguage]);

  function applyLanguage(code: string) {
    if (type === LANGUAGE_TYPE.DISPLAY) {
      setDisplayLanguage(code);
      return;
    }
    if (type === LANGUAGE_TYPE.AUDIO) {
      setAudioLanguage(code);
      return;
    }
    setSubTitleLanguage(code);
  }

  function selectLanguage(event: MouseEvent) {
    event.preventDefault();
    const element = event.currentTarget as HTMLInputElement;
    const { code = '' } = element.dataset;

    setLanguage(code);

    if (language === code) return;

    if (languagesHandle) {
      languagesHandle(code, type, applyLanguage);
      return;
    }

    applyLanguage(code);
  }

  return (
    <ul ref={handleRef} className={cn(isDropDown ? styles.listDropDown : styles.list)}>
      {list.map((lang: any) => {
        const languageNative = languageNativeNameByCode(lang.code);
        const noLanguageNative = intl.formatMessage({ id: `LanguagesList.empty.${type}`, defaultMessage: 'Language is not provided' });
        const resolvedString = languageNative || noLanguageNative;

        return (
          <li key={lang.code}>
            <button
              data-code={lang.code}
              aria-label={resolvedString}
              onClick={selectLanguage}
              type='button'
              tabIndex={0}
            >
              <span aria-hidden="true">
                <SVG className={cn({ [styles.active]: lang.code === language })} src={checkIcon} />
                {resolvedString}
              </span>
            </button>
          </li>
        )})}
    </ul>
  );
}

export default LanguagesList;
